<template>
    <div class="yield-page">
        <breadcrumb :items="breadcrumbs"></breadcrumb>
        <v-container fluid class="yield-container">
            <div class="report-page-header">
                <div class="title-row">
                    <h2>{{ $t("reports.yieldPrediction") }}</h2>
                    <v-btn small rounded color="primary" class="guide-button" @click="showGuide = true">
                        <v-icon left small>mdi-help-circle-outline</v-icon>
                        {{ $t("reports.aiReportGuide") }}
                    </v-btn>
                </div>
                <v-spacer></v-spacer>
                <div class="header-actions">
                    <v-btn outlined color="primary" @click="$router.push({ name: 'aiReportsDashboard' })">
                        {{ $t("reports.backToDashboard") }}
                    </v-btn>
             
                    <v-btn color="primary" dark @click="createNewReport">
                        {{ $t("reports.createNewReport") }}
                    </v-btn>
                </div>
            </div>
            <v-card elevation="0" class="report-list-card">
                <v-card-text>
                    <div class="mb-4">
                        <h3 class="mb-3">{{ $t("reports.listOfReports") }}</h3>
                    </div>
                    <v-data-table class="my-awesome-table" :headers="tableHeaders" :items="items" :items-per-page="tableOptions.itemsPerPage"
                        v-model="selected" :loading="loading" :loading-text="$t('reports.loadingYieldPredictions')" :footer-props="{
                            'items-per-page-options': [10, 25, 50],
                            'show-first-last-page': true,
                            'show-current-page': true,
                        }" :options.sync="tableOptions" :server-items-length="totalItems" hide-default-footer>
                        <template v-slot:top>
                            <div class="table-toolbar">
                                <v-text-field
                                    v-model="search"
                                    :placeholder="$t('oma.search')"
                                    :disabled="false"
                                    outlined
                                    dense
                                    hide-details
                                    prepend-inner-icon="mdi-magnify"
                                    class="search-field"
                                />
                                <div class="crop-filter">
                                    <label>{{ $t("reports.crop") || "Crop" }}</label>
                                    <v-select
                                        v-model="selectedCrop"
                                        :items="crops"
                                        outlined
                                        dense
                                        item-value="id"
                                        item-text="name"
                                        :placeholder="$t('reports.select') || 'Select'"
                                        clearable
                                        hide-details
                                    />
                                </div>
                                
                                <v-spacer></v-spacer>
                                
                                <div class="record-count">
                                    {{ currentPageItems }} {{ $t('of') }} {{ totalItems }}
                                </div>
                                <v-btn class="toolbar-icon" icon small outlined color="primary" @click="pageChange(false)"
                                    :disabled="tableOptions.page <= 1">
                                    <v-icon small>mdi-chevron-left</v-icon>
                                </v-btn>
                                <v-btn class="toolbar-icon" icon small outlined color="primary" @click="pageChange(true)"
                                    :disabled="tableOptions.page >= totalPages">
                                    <v-icon small>mdi-chevron-right</v-icon>
                                </v-btn>
                                <v-menu v-model="settingsMenu" :close-on-content-click="false" left offset-y min-width="380">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn class="toolbar-icon" icon small outlined color="primary" v-bind="attrs" v-on="on">
                                            <v-icon small>mdi-cog-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-card class="settings-card">
                                        <v-card-title>{{ $t("reports.customizeTable") }}</v-card-title>
                                        <v-card-text>
                                            <label>{{ $t("reports.recordsPerPage") }}</label>
                                            <v-select v-model="draftItemsPerPage" :items="[10, 25, 50]" outlined dense hide-details />
                                            <v-divider class="my-4" />
                                            <label>{{ $t("reports.visibleColumns") }}</label>
                                            <v-checkbox
                                                v-for="header in customizableHeaders"
                                                :key="header.value"
                                                v-model="draftSelectedHeaders"
                                                :label="header.text"
                                                :value="header.value"
                                                dense
                                                hide-details
                                            />
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-btn text color="primary" @click="resetTableSettings">{{ $t("reports.restoreDefault") }}</v-btn>
                                            <v-spacer />
                                            <v-btn text @click="settingsMenu = false">{{ $t("cancel") }}</v-btn>
                                            <v-btn color="primary" @click="applyTableSettings">{{ $t("reports.apply") }}</v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-menu>
                                <v-btn class="toolbar-icon" icon small outlined color="primary" @click="handleDownload">
                                    <v-icon small>mdi-download</v-icon>
                                </v-btn>
                                <v-btn class="toolbar-icon" icon small outlined color="primary" @click="handlePrint">
                                    <v-icon small>mdi-printer</v-icon>
                                </v-btn>
                            </div>
                        </template>
                        <template v-slot:no-data v-if="items.length < 1">
                            {{ $t("reports.noYieldPredictionReportsFound") }}
                        </template>
                        <template v-if="items.length > 0" v-slot:body="{ items }">
                            <tbody>
                                <tr v-for="item in items" :key="item.id" @mouseover="selectItem(item)"
                                    @mouseleave="unSelectItem()" style="position: relative;">
                                    <template>
                                        <td v-if="isColumnVisible('id')">{{ item.id }}</td>
                                        <td v-if="isColumnVisible('farmName')">
                                            <div>{{ item.farmName || 'N/A' }}</div>
                                            <div class="text-caption grey--text">{{ item.farmId || 'N/A' }}</div>
                                        </td>
                                        <td v-if="isColumnVisible('producerName')">
                                            {{ item.producerName || (item.user ? `${item.user.firstName} ${item.user.lastName}` : 'N/A') }}
                                        </td>
                                        <td v-if="isColumnVisible('crop')" class="capitlize">
                                            {{ item.crop || item.cropType || 'N/A' }}
                                        </td>
                                        <td v-if="isColumnVisible('dateOfReport')" class="capitlize">
                                            {{ formatDate(item.dateOfReport || item.createdAt || item.issuedDate) }}
                                        </td>
                                        <td>
                                            <div style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%);">
                                                <v-menu offset-y>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn 
                                                            icon 
                                                            x-small 
                                                            v-bind="attrs" 
                                                            v-on="on"
                                                            class="menu-dots-btn"
                                                        >
                                                            <v-icon>mdi-dots-vertical</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <v-list>
                                                        <v-list-item @click="viewReport(item)">
                                                            <v-list-item-title>{{ $t("show") }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item @click="confirmDeleteReport(item)">
                                                            <v-list-item-title>{{ $t("delete") }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item @click="downloadReport(item)">
                                                            <v-list-item-title>{{ $t("reports.download") }}</v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>
                                            </div>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="deleteDialog" persistent max-width="500">
                <v-card>
                    <v-toolbar color="error" elevation="0">
                        <v-toolbar-title class="white--text">
                            {{ $t('reports.deleteConfirmation') }}
                        </v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn color="white" icon @click="deleteDialog = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <v-card-text class="pt-5 black--text">
                        <div class="text-center mb-4">
                            <v-icon size="60" color="error">mdi-alert-circle-outline</v-icon>
                        </div>
                        <p class="text-body-1 text-center">
                            {{ $t('reports.confirmDeleteReport') }}
                        </p>
                        <v-divider class="mt-5"></v-divider>
                        <v-card-actions class="pt-4">
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                outlined
                                @click="deleteDialog = false"
                            >
                                {{ $t('cancel') }}
                            </v-btn>
                            <v-btn
                                depressed
                                color="error"
                                @click="deleteReport"
                                :loading="deleting"
                            >
                                {{ $t('delete') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <yield-prediction-guide-dialog v-model="showGuide" />
        </v-container>
    </div>
</template>

<script>
import DownloadMixin from "@/mixins/DownloadMixin";
import DateMixin from "@/mixins/DateMixin";
import loadingMixin from "@/mixins/LoadingMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import YieldPredictionService from '@/_services/YieldPredictionService';
import YieldPredictionGuideDialog from './components/YieldPredictionGuideDialog.vue';

export default {
    computed: {
        currentPageItems() {
            return Math.min(this.tableOptions.page * this.tableOptions.itemsPerPage, this.totalItems);
        },
        headers() {
            return [
                {
                    text: this.$t('reports.reportId'),
                    value: "id",
                    sortable: true,
                    class: "text-no-wrap yield-table-header",
                },
                {
                    text: this.$t('reports.farmAndFarmId'),
                    align: "start",
                    sortable: true,
                    value: "farmName",
                    class: "text-no-wrap yield-table-header",
                },
                {
                    text: this.$t('reports.producerName'),
                    value: "producerName",
                    sortable: true,
                    class: "text-no-wrap yield-table-header"
                },
                {
                    text: this.$t('reports.crop'),
                    value: "crop",
                    sortable: true,
                    class: "text-no-wrap yield-table-header"
                },
                {
                    text: this.$t('reports.dateOfReport'),
                    value: "dateOfReport",
                    sortable: true,
                    class: "text-no-wrap yield-table-header",
                },
                {
                    text: "",
                    value: "action",
                    align: "end",
                    sortable: false,
                    class: "text-no-wrap",
                    width: "80px",
                },
            ];
        },
        customizableHeaders() {
            return this.headers.filter(header => header.value !== "action");
        },
        tableHeaders() {
            return this.headers.filter(
                header => header.value === "action" || this.selectedHeaders.includes(header.value)
            );
        }
    },
    mounted() {
        this.loading = false;
        this.getYieldPredictions();
        this.getCrops();
    },
    watch: {
        tableOptions: {
            async handler() {
                await this.getYieldPredictions();
            },
            deep: true,
        },
        search: {
            handler() {
                this.debounceSearch();
            }
        },
        selectedCrop: {
            handler() {
                this.getYieldPredictions();
            }
        }
    },
    components: {
        YieldPredictionGuideDialog,
    },
    methods: {
        createNewReport() {
            // Navigate to create new report page
            this.$router.push({ name: 'yieldPredictionCreate' }).catch(() => {});
        },
        isColumnVisible(value) {
            return this.selectedHeaders.includes(value);
        },
        resetTableSettings() {
            this.draftItemsPerPage = 10;
            this.draftSelectedHeaders = this.customizableHeaders.map(header => header.value);
        },
        applyTableSettings() {
            this.tableOptions.itemsPerPage = this.draftItemsPerPage;
            this.tableOptions.page = 1;
            this.selectedHeaders = [...this.draftSelectedHeaders];
            this.settingsMenu = false;
        },
        selectItem(item) {
            this.selectedItem = item;
        },
        unSelectItem() {
            this.selectedItem = false;
        },
        async getCrops(){
            try {
                await YieldPredictionService.getCropList().then(res => {
                    if (res.data && res.data.crops && Array.isArray(res.data.crops)) {
                        this.crops = res.data.crops.map(crop => ({
                            id: crop,
                            name: crop
                        }));
                    } else {
                        this.crops = [
                        ];
                    }
                }).catch(err => {
                    console.error('Error fetching crops:', err);
                    this.crops = [];
                });
            } catch (error) {
                console.error('Error fetching crops:', error);
                this.crops = []
            }
        },
        async getYieldPredictions(){
            this.loading = true;
            try {
                await YieldPredictionService.getYieldPredictionLists({
                    limit: this.tableOptions.itemsPerPage,
                    search: this.search,
                    page: this.tableOptions.page,
                    crop: this.selectedCrop
                }).then(res => {
                    this.items = res.data.rows || []
                    this.totalItems = res.data.count || 0
                    this.totalPages = Math.ceil(
                        this.totalItems / this.tableOptions.itemsPerPage
                    );
                    this.loading = false;
                }).catch(err => {
                    console.error('Error fetching yield predictions:', err)
                    this.loading = false;
                    this.items = []
                    this.totalItems = 0
                })
            } catch (error) {
                console.error('Error fetching yield predictions:', error);
                this.loading = false;
                this.items = []
                this.totalItems = 0
            }
        },
        debounceSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.tableOptions.page = 1;
                this.getYieldPredictions();
            }, 500);
        },
        pageChange(forward) {
            if (forward) {
                if (this.tableOptions.page < this.totalPages) {
                    this.tableOptions.page = this.tableOptions.page + 1;
                }
            } else {
                if (this.tableOptions.page > 1) {
                    this.tableOptions.page = this.tableOptions.page - 1;
                }
            }
        },
        handleDownload() {
            const visibleHeaders = this.customizableHeaders.filter(header =>
                this.selectedHeaders.includes(header.value)
            );
            const escape = value => `"${String(value ?? "").replace(/"/g, '""')}"`;
            const lines = [
                visibleHeaders.map(header => escape(header.text)).join(","),
                ...this.items.map(item =>
                    visibleHeaders.map(header => {
                        if (header.value === "dateOfReport") {
                            return escape(this.formatDate(item.dateOfReport || item.createdAt));
                        }
                        if (header.value === "producerName" && !item.producerName && item.user) {
                            return escape(`${item.user.firstName || ""} ${item.user.lastName || ""}`.trim());
                        }
                        return escape(item[header.value]);
                    }).join(",")
                ),
            ];
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
            link.download = "yield-prediction-reports.csv";
            link.click();
            URL.revokeObjectURL(link.href);
        },
        handlePrint() {
            // Handle print action
            window.print();
        },
        viewReport(item) {
            // Navigate to view report page
            this.$router.push({ 
                name: 'yieldPredictionView', 
                params: { id: item.id } 
            }).catch(() => {});
        },
        async downloadReport(item) {
            try {
                this.startLoading();
                const response = await YieldPredictionService.downloadReport(item.id, new FormData());
                this.downloadBlob(response.data, `yield-prediction-report-${item.id}.pdf`);
                this.$notify({
                    title: this.$t('reports.download'),
                    text: this.$t('reports.pdfGeneratedSuccessfully'),
                    type: 'success',
                });
            } catch (error) {
                console.error('Error downloading report:', error);
                this.$notify({
                    title: this.$t('error') || 'Error',
                    text: this.$t('reports.failedToGeneratePdf'),
                    type: 'error',
                });
            } finally {
                this.stopLoading();
            }
        },
        confirmDeleteReport(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },
        async deleteReport() {
            if (!this.itemToDelete) return;
            
            this.deleting = true;
            try {
                await YieldPredictionService.deleteYieldPrediction(this.itemToDelete.id);
                this.deleteDialog = false;
                this.itemToDelete = null;
                this.$notify({
                    title: this.$t('success'),
                    text: this.$t('reports.reportDeletedSuccessfully'),
                    type: 'success',
                });
                // Refresh the list
                await this.getYieldPredictions();
            } catch (error) {
                console.error('Error deleting report:', error);
                this.$notify({
                    title: this.$t('error') || 'Error',
                    text: this.$t('reports.failedToDeleteReport'),
                    type: 'error',
                });
            } finally {
                this.deleting = false;
            }
        }
    },
    data() {
        return {
            showGuide: false,
            settingsMenu: false,
            selectedHeaders: ["id", "farmName", "producerName", "crop", "dateOfReport"],
            draftSelectedHeaders: ["id", "farmName", "producerName", "crop", "dateOfReport"],
            draftItemsPerPage: 10,
            deleteDialog: false,
            deleting: false,
            itemToDelete: null,
            totalItems: 0,
            totalPages: 1,
            crops: [],
            selectedCrop: null,
            searchTimeout: null,
            tableOptions: {
                page: 1,
                itemsPerPage: 10
            },
            search: "",
            selected: [],
            items: [],
            selectedItem: false,
            loading: false,
            breadcrumbs: [
                {
                    text: this.$t("dashboard"),
                    disabled: false,
                    to: "/",
                },
                {
                    text: this.$t("reports.yieldPrediction") || "Yield Prediction",
                    disabled: true,
                    to: "",
                },
            ],
        };
    },
    mixins: [loadingMixin, getPermittedActions, DownloadMixin, DateMixin]
};
</script>

<style lang="scss" scoped>
.yield-page {
    min-height: 100%;
    background: #f5f6f8;
}

.yield-container {
    padding: 20px 28px 40px;
}

.report-page-header,
.title-row,
.header-actions,
.table-toolbar {
    display: flex;
    align-items: center;
}

.report-page-header {
    margin-bottom: 18px;
    gap: 16px;
}

.title-row,
.header-actions {
    gap: 12px;
}

.title-row h2 {
    color: #182126;
    font-size: 24px;
    line-height: 1.2;
}

.guide-button {
    box-shadow: none;
    font-size: 10px;
}

.header-actions .v-btn {
    min-width: 145px;
    height: 40px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 13px;
    font-weight: 600;
}

.report-list-card {
    min-height: 520px;
    padding: 4px 14px 24px;
    border-radius: 2px;
}

.table-toolbar {
    gap: 10px;
    flex-wrap: wrap;
    align-items: flex-end;
    margin: 20px 0 18px;
}

.search-field {
    max-width: 250px;
    margin: 0;
}

.search-field::v-deep .v-input__control,
.crop-filter::v-deep .v-input__control {
    min-height: 40px;
}

.search-field::v-deep .v-input__slot,
.crop-filter::v-deep .v-input__slot {
    margin-bottom: 0 !important;
    min-height: 40px;
}

.crop-filter {
    width: 250px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    label {
        display: block;
        margin-bottom: 4px;
        color: #343c40;
        font-size: 16px;
        line-height: 1.2;
    }

    .v-select {
        margin: 0;
    }
}

.record-count {
    color: #727b80;
    font-size: 16px;
}

.toolbar-icon {
    width: 32px;
    height: 32px;
    margin: 0 2px;
    border-color: #79c4a9 !important;
}

.settings-card {
    label {
        display: block;
        margin-bottom: 8px;
        color: #384249;
        font-size: 12px;
        font-weight: 600;
    }
}

.my-awesome-table {
    table-layout: fixed;
}

::v-deep .my-awesome-table thead tr {
    background: #147a49;
}

::v-deep .my-awesome-table thead th {
    height: 44px;
    color: #fff !important;
    font-size: 11px;
}

::v-deep .my-awesome-table tbody td {
    height: 48px;
    color: #30393e;
    font-size: 11px;
}

::v-deep .my-awesome-table tbody tr:nth-child(even) {
    background: #f8f8f8;
}
::v-deep .v-data-table thead th .v-icon {
    color: white !important;
}

::v-deep .menu-dots-btn {
    opacity: 0.7;
}

::v-deep .menu-dots-btn:hover {
    opacity: 1;
}

::v-deep .v-data-table tbody tr:hover {
    background-color: #f5f5f5;
}

::v-deep .v-data-table tbody tr {
    cursor: pointer;
}

@media (max-width: 900px) {
    .report-page-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .report-page-header .v-spacer {
        display: none;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }
}
</style>